import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Pencil, ArrowLeft, ClipboardList } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorState from '../../components/ErrorState';
import * as pkgApi from '../../api/packages.api';
import { useAuth } from '../../hooks/useAuth';
import { ROLES } from '../../utils/roles';
import { errorMessage } from '../../api/client';
import { formatMoney, formatDate } from '../../utils/format';

function PackageDetails() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const isAdmin = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(user?.role);
  const isPatient = user?.role === ROLES.USER;

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    pkgApi.getPackage(id)
      .then(setPkg)
      .catch((e) => setErr(errorMessage(e)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <LoadingSpinner size="lg" />;
  if (err) return <ErrorState message={err} action={<button className="btn" onClick={() => navigate(-1)}><ArrowLeft size={14} /> Back</button>} />;
  if (!pkg) return null;

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Packages', to: '/packages' }, { label: pkg.name }]}
        title={pkg.name}
        subtitle={pkg.description}
        actions={
          <>
            {isPatient && (
              <Link to={`/orders/checkout/${pkg._id}`} className="btn btn-primary">
                <ShoppingCart size={16} /> Buy this package
              </Link>
            )}
            {isAdmin && (
              <Link to={`/packages/${pkg._id}/edit`} className="btn btn-outline">
                <Pencil size={16} /> Edit
              </Link>
            )}
          </>
        }
      />

      {pkg.bannerUrl && (
        <div
          className="card"
          style={{ padding: 0, overflow: 'hidden', marginBottom: 'var(--s-4)' }}
        >
          <img
            src={pkgApi.packageBannerUrl(pkg)}
            alt={`${pkg.name} banner`}
            style={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: 'block' }}
          />
        </div>
      )}

      <div className="grid grid-2">
        <div className="card">
          <div className="card-title">
            <h3>Package details</h3>
            {pkg.isActive ? <StatusBadge tone="success">Active</StatusBadge> : <StatusBadge tone="neutral">Inactive</StatusBadge>}
          </div>
          <dl className="kvs">
            <dt>Slug</dt><dd className="cell-mono">{pkg.slug || '—'}</dd>
            <dt>Price</dt><dd className="bold text-xl">{formatMoney(pkg.price, pkg.currency || 'MYR')}</dd>
            <dt>Currency</dt><dd>{pkg.currency || 'MYR'}</dd>
            <dt>Created</dt><dd>{formatDate(pkg.createdAt)}</dd>
            <dt>Updated</dt><dd>{formatDate(pkg.updatedAt)}</dd>
          </dl>
        </div>

        <div className="card">
          <div className="card-title">
            <h3>What's included</h3>
            <span className="muted text-sm">{pkg.tests?.length || 0} tests</span>
          </div>
          {pkg.tests?.length ? (
            <div className="col" style={{ gap: 0 }}>
              {pkg.tests.map((t, i) => (
                <div key={i} className="list-row">
                  <div className="row" style={{ gap: 10 }}>
                    <ClipboardList size={16} color="var(--brand-700)" />
                    <span>{t}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="muted">No specific tests listed for this package.</div>
          )}
        </div>
      </div>

      {isPatient && (
        <div className="callout" style={{ marginTop: 'var(--s-4)' }}>
          Ready to book? Click <strong>Buy this package</strong> above and you'll be guided through
          a quick checkout. Payments are processed securely via Curlec.
        </div>
      )}
    </>
  );
}

export default PackageDetails;
